import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from 'react';

import CardPaymentForm, {CardInfo, CardPaymentFormRef} from './CardPaymentForm';
import {CashiaInfo, CashiaWalletFormRef} from './CashiaWalletForm';
import {MPesaInfo, MPesaPaymentFormRef} from './MpesaPaymentForm';
import Mpesa from '../../../../assets/Mpesa.svg';
import MasterCard from '../../../../assets/icons/MasterCard.svg';
import Visa from '../../../../assets/icons/VisaCard.svg';
import Cards from '../../../../assets/icons/card.svg';
import Wallet from '../../../../assets/icons/wallet.svg';
import Cashia from '../../../../assets/logos/cashia_logo_pink.svg';
import Mobile from '../../../../assets/mobile.svg';
import CustomRadioInput from '../../../../components/common/CustomRadioInput';

export type PaymentMethod = 'card' | 'mpesa' | 'cashia';

// Export ref interface for parent to access validation
export interface PaymentMethodSelectorRef {
  validateCurrentMethod: () => boolean;
  hasErrors: () => boolean;
}

const PaymentMethodSelector = forwardRef<
  PaymentMethodSelectorRef,
  {
    terminalId: string;
    keyId: string;
    secretKey: string;
    amount: number;
    currency: string;
    paymentMethod: PaymentMethod;
    setPaymentMethod: React.Dispatch<React.SetStateAction<PaymentMethod>>;
    cardInfo: CardInfo;
    setCardInfo: React.Dispatch<React.SetStateAction<CardInfo>>;
    mpesaInfo: MPesaInfo;
    setMPesaInfo: React.Dispatch<React.SetStateAction<MPesaInfo>>;
    cashiaInfo: CashiaInfo;
    setCashiaInfo: React.Dispatch<React.SetStateAction<CashiaInfo>>;
    onBinCheck?: (requiresAddress: boolean) => void;
  }
>((props, ref) => {
  const {
    keyId,
    secretKey,
    amount,
    currency,
    paymentMethod,
    setPaymentMethod,
    cardInfo,
    setCardInfo,
    onBinCheck,
    terminalId: _terminalId,
    mpesaInfo: _mpesaInfo,
    setMPesaInfo: _setMPesaInfo,
    cashiaInfo: _cashiaInfo,
    setCashiaInfo: _setCashiaInfo,
  } = props;

  // Refs to access child component validation
  const cardFormRef = useRef<CardPaymentFormRef>(null);
  const mpesaFormRef = useRef<MPesaPaymentFormRef>(null);
  const cashiaFormRef = useRef<CashiaWalletFormRef>(null);

  // Expose validation methods to parent
  useImperativeHandle(ref, () => ({
    validateCurrentMethod: () => {
      if (paymentMethod === 'card') {
        return cardFormRef.current ? cardFormRef.current.validateAll() : false;
      } else if (paymentMethod === 'mpesa') {
        return mpesaFormRef.current
          ? mpesaFormRef.current.validateAll()
          : false;
      } else if (paymentMethod === 'cashia') {
        return cashiaFormRef.current
          ? cashiaFormRef.current.validateAll()
          : false;
      }
      return false;
    },

    hasErrors: () => {
      if (paymentMethod === 'card') {
        return cardFormRef.current ? cardFormRef.current.hasErrors() : false;
      } else if (paymentMethod === 'mpesa') {
        return mpesaFormRef.current ? mpesaFormRef.current.hasErrors() : false;
      } else if (paymentMethod === 'cashia') {
        return cashiaFormRef.current
          ? cashiaFormRef.current.hasErrors()
          : false;
      }
      return false;
    },
  }));
  const isKES = currency === 'KES';

  function getEnabledPaymentMethods(isKESFlag: boolean, amt: number) {
    if (!isKESFlag || amt > 250000) {
      return ['card', 'cashia'] as const;
    }
    if (isKESFlag && amt < 100) {
      // return ['mpesa', 'cashia'] as const;
      return ['cashia'] as const;
    }
    // return ['card', 'mpesa', 'cashia'] as const;
    return ['card', 'cashia'] as const;
  }

  const ENABLED_PAYMENT_METHODS = useMemo(
    () => getEnabledPaymentMethods(isKES, amount),
    [isKES, amount]
  );
  const PAYMENT_METHOD_DETAILS: Record<
    string,
    {label: string; icon?: JSX.Element; logo?: JSX.Element}
  > = {
    card: {
      label: 'Card',
      icon: <img src={Cards} alt="Cards" className="h-4 mr-2" />,
      logo: (
        <>
          <img src={MasterCard} alt="Mastercard" className="h-4" />
          <img src={Visa} alt="Visa" className="h-4 ml-1" />
        </>
      ),
    },
    mpesa: {
      label: 'M-Pesa',
      icon: <img src={Mobile} alt="mobile" className="h-4 mr-2" />,
      logo: <img src={Mpesa} alt="M-Pesa" className="ml-auto h-4" />,
    },
    cashia: {
      label: 'Cashia',
      icon: <img src={Wallet} alt="wallet" className="h-4 mr-2" />,
      logo: <img src={Cashia} alt="Cashia" className="ml-auto h-4" />,
    },
  };

  useEffect(() => {
    setPaymentMethod(ENABLED_PAYMENT_METHODS[0]);
  }, [ENABLED_PAYMENT_METHODS, setPaymentMethod]);

  return (
    <div className="bg-transparent w-full mb-6">
      <h3 className="text-[16px] font-semibold mb-4 text-headlineGrayscale">
        Payment Method
      </h3>

      <div className="rounded-lg outline outline-[1px] outline-offset-[-0.80px] outline-subtleGrey inline-flex flex-col justify-start items-start overflow-hidden w-full">
        {ENABLED_PAYMENT_METHODS.map((item, idx) => (
          <div
            key={idx}
            className={`p-3 lg:p-4 w-full ${
              idx !== ENABLED_PAYMENT_METHODS.length - 1
                ? 'border-b-[1px] border-subtleGrey'
                : ''
            }`}>
            <CustomRadioInput
              checked={paymentMethod === item}
              handleChange={() => setPaymentMethod(item)}
              item={item}
              methodDetails={PAYMENT_METHOD_DETAILS}
            />

            {item === 'card' && paymentMethod === 'card' && (
              <div className="mt-3 mb-0 lg:mb-1">
                <CardPaymentForm
                  keyId={keyId}
                  secretKey={secretKey}
                  ref={cardFormRef}
                  cardInfo={cardInfo}
                  setCardInfo={setCardInfo}
                  onBinCheck={onBinCheck}
                />
              </div>
            )}
            {/* {item === 'mpesa' && paymentMethod === 'mpesa' && (
              <div className="mt-3 mb-0 lg:mb-1">
                <MPesaPaymentForm
                  ref={mpesaFormRef}
                  mpesaInfo={mpesaInfo}
                  setMPesaInfo={setMPesaInfo}
                />
              </div>
            )} */}

            {/* {item === 'cashia' && paymentMethod === 'cashia' && (
              <div className="mt-3 mb-0 lg:mb-1">
                <CashiaWalletForm
                  ref={cashiaFormRef}
                  cashiaInfo={cashiaInfo}
                  setCashiaInfo={setCashiaInfo}
                  reference={reference}
                />
              </div>
            )} */}
          </div>
        ))}
      </div>
    </div>
  );
});

PaymentMethodSelector.displayName = 'PaymentMethodSelector';

export default PaymentMethodSelector;
